import { Button, Col, InputNumber, Row, Space, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import ProjectSelect from '../ProjectSelect';

const { Text } = Typography;

const EMPTY_ROW = () => ({ project: null, split_percent: '0' });

const round2 = (n) => Math.round(n * 100) / 100;

const ProjectSplitsField = ({
  value = [],
  onChange,
  readOnly = false,
  hidden = false,
  amount = null,
}) => {
  if (hidden) return null;

  const rows = Array.isArray(value) && value.length > 0
    ? value
    : [EMPTY_ROW(), EMPTY_ROW()];

  const emit = (next) => onChange?.(next);

  const setRow = (i, patch) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    emit(next);
  };

  const addRow = () => emit([...rows, EMPTY_ROW()]);

  const removeRow = (i) => {
    if (rows.length <= 2) return;
    emit(rows.filter((_, idx) => idx !== i));
  };

  const total = amount != null && amount !== '' ? Number(amount) : null;
  const hasTotal = total != null && total > 0;

  // € amount per row is a convenience over the canonical split_percent.
  // We keep the typed amount in `split_amount` so the input shows exactly what
  // the user enters (deriving it from the rounded percent on every keystroke
  // would mangle multi-digit typing). `split_amount` is stripped before save.
  const euroForRow = (row) =>
    row.split_amount != null
      ? row.split_amount
      : hasTotal
      ? round2((Number(row.split_percent || 0) / 100) * total)
      : null;
  const pctFromEuro = (v) =>
    hasTotal ? round2(((Number(v) || 0) / total) * 100) : 0;
  const setEuro = (i, v) => {
    if (!hasTotal) return;
    setRow(i, {
      split_amount: v == null ? null : Number(v),
      split_percent: String(pctFromEuro(v)),
    });
  };
  const setPercent = (i, v) => {
    const pct = v == null ? 0 : Number(v);
    setRow(i, {
      split_percent: String(pct),
      split_amount: hasTotal ? round2((pct / 100) * total) : null,
    });
  };

  const sum = rows.reduce((a, r) => a + Number(r.split_percent || 0), 0);
  const sumDisplay = round2(sum); // avoid floating-point noise like 68.2100000001
  const sumValid = Math.abs(sum - 100) < 0.01;
  const sumEuros = hasTotal ? round2((sum / 100) * total) : null;

  return (
    <div>
      {rows.map((row, i) => (
        <Row key={i} gutter={8} style={{ marginBottom: 8 }} align="middle">
          <Col flex="auto">
            <ProjectSelect
              value={row.project}
              onChange={(v) => setRow(i, { project: v })}
              disabled={readOnly}
            />
          </Col>
          <Col style={{ width: 150 }}>
            <InputNumber
              value={euroForRow(row)}
              onChange={(v) => setEuro(i, v)}
              min={0}
              step={0.01}
              precision={2}
              disabled={readOnly || !hasTotal}
              addonAfter="€"
              style={{ width: '100%' }}
            />
          </Col>
          <Col style={{ width: 120 }}>
            <InputNumber
              value={row.split_percent != null ? Number(row.split_percent) : 0}
              onChange={(v) => setPercent(i, v)}
              min={0}
              max={100}
              step={0.01}
              disabled={readOnly}
              addonAfter="%"
              style={{ width: '100%' }}
            />
          </Col>
          <Col>
            <Button
              icon={<DeleteOutlined />}
              danger
              type="text"
              onClick={() => removeRow(i)}
              disabled={readOnly || rows.length <= 2}
            />
          </Col>
        </Row>
      ))}

      {!hasTotal && !readOnly && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Vnesite znesek transakcije za razdelitev po znesku.
        </Text>
      )}

      {!readOnly && (
        <Space style={{ marginTop: 8, width: '100%', justifyContent: 'space-between' }}>
          <Button icon={<PlusOutlined />} onClick={addRow} type="dashed">
            Dodaj projekt
          </Button>
          <Text type={sumValid ? 'success' : 'danger'} strong>
            Skupaj: {sumDisplay}%{hasTotal && ` (${sumEuros} € / ${round2(total)} €)`}{!sumValid && ' (mora biti 100%)'}
          </Text>
        </Space>
      )}

      {readOnly && (
        <Text type={sumValid ? 'success' : 'danger'} strong>
          Skupaj: {sumDisplay}%{hasTotal && ` (${sumEuros} € / ${round2(total)} €)`}
        </Text>
      )}
    </div>
  );
};

export default ProjectSplitsField;
